import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { UserProfileService, UserProfileData } from '../services/user-profile.service';
import { LoggerService } from '../../../shared/services/logger.service';

interface UseUserProfileResult {
  profileData: UserProfileData | null;
  isLoading: boolean;
  error: string | null;
  saveProfile: (data: UserProfileData) => Promise<void>;
  updateProfile: (updates: Partial<UserProfileData>) => Promise<void>;
  reloadProfile: () => Promise<void>;
}

export const useUserProfile = (): UseUserProfileResult => {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState<UserProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProfile = useCallback(async () => {
    if (!user?.uid) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const savedProfile = await UserProfileService.getUserProfile(user.uid);
      
      if (savedProfile) {
        setProfileData(savedProfile);
      } else {
        // Crear perfil por defecto si no existe
        const defaultProfile = UserProfileService.createDefaultProfile(user);
        setProfileData(defaultProfile);
        // Opcional: guardarlo automáticamente
        await UserProfileService.saveUserProfile(user.uid, defaultProfile);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error cargando perfil';
      setError(errorMessage);
      LoggerService.error('Error loading user profile', 'useUserProfile', err);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const saveProfile = async (data: UserProfileData): Promise<void> => {
    if (!user?.uid) {
      throw new Error('Usuario no autenticado');
    }

    setIsLoading(true);
    setError(null);

    try {
      await UserProfileService.saveUserProfile(user.uid, data);
      setProfileData(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error guardando perfil';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (updates: Partial<UserProfileData>): Promise<void> => {
    if (!user?.uid || !profileData) {
      throw new Error('Usuario no autenticado o perfil no cargado');
    }

    setIsLoading(true);
    setError(null);

    try {
      await UserProfileService.updateUserProfile(user.uid, updates);
      const updatedProfile = { ...profileData, ...updates };
      setProfileData(updatedProfile);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error actualizando perfil';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const reloadProfile = async (): Promise<void> => {
    await loadProfile();
  };

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  return {
    profileData,
    isLoading,
    error,
    saveProfile,
    updateProfile,
    reloadProfile
  };
};